import React, { useEffect, useRef, useState } from 'react';

export const CustomCursor: React.FC = () => {
  const dotRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  const [cursorText, setCursorText] = useState<string>('');
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [isPointerDevice, setIsPointerDevice] = useState<boolean>(false);

  useEffect(() => {
    // Check if device has fine pointer (mouse/trackpad, not touch)
    if (window.matchMedia('(pointer: fine)').matches) {
      setIsPointerDevice(true);
    } else {
      return;
    }

    let mouseX = window.innerWidth / 2;
    let mouseY = window.innerHeight / 2;
    let ringX = mouseX;
    let ringY = mouseY;
    let animFrameId: number;

    const handleMouseMove = (e: MouseEvent) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      setIsVisible(true);

      if (dotRef.current) {
        dotRef.current.style.transform = `translate3d(${mouseX}px, ${mouseY}px, 0)`;
      }

      // Detect interactive targets under cursor
      const target = e.target as HTMLElement | null;
      if (!target) return;

      const interactiveEl = target.closest('a, button, [data-cursor], input, [role="button"]');
      if (interactiveEl) {
        setIsHovered(true);
        const cursorAttr = interactiveEl.getAttribute('data-cursor');
        if (cursorAttr) {
          setCursorText(cursorAttr.toUpperCase());
        } else {
          setCursorText('');
        }
      } else {
        setIsHovered(false);
        setCursorText('');
      }
    };

    const handleMouseLeave = () => {
      setIsVisible(false);
    };

    // Smooth inertia interpolation for outer ring (lerp)
    const renderLoop = () => {
      const lerpFactor = 0.16;
      ringX += (mouseX - ringX) * lerpFactor;
      ringY += (mouseY - ringY) * lerpFactor;

      if (ringRef.current) {
        ringRef.current.style.transform = `translate3d(${ringX}px, ${ringY}px, 0)`;
      }

      animFrameId = requestAnimationFrame(renderLoop);
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    document.addEventListener('mouseleave', handleMouseLeave);
    animFrameId = requestAnimationFrame(renderLoop);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', handleMouseLeave);
      cancelAnimationFrame(animFrameId);
    };
  }, []);

  if (!isPointerDevice) return null;

  return (
    <div
      className={`fixed inset-0 pointer-events-none z-50 transition-opacity duration-300 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
      aria-hidden="true"
    >
      {/* Central precise dot */}
      <div
        ref={dotRef}
        className="fixed top-0 left-0 -ml-1 -mt-1 w-2 h-2 rounded-full bg-cream-100 mix-blend-difference will-change-transform"
      />

      {/* Lagging inertia ring */}
      <div
        ref={ringRef}
        className={`fixed top-0 left-0 -ml-5 -mt-5 rounded-full border transition-all duration-200 flex items-center justify-center will-change-transform ${
          isHovered
            ? 'w-16 h-16 -ml-8 -mt-8 border-sunset-gold/80 bg-sunset-gold/15'
            : 'w-10 h-10 border-white/25 bg-transparent'
        }`}
      >
        {cursorText && (
          <span className="text-[9px] font-mono font-semibold tracking-wider text-sunset-gold select-none">
            {cursorText}
          </span>
        )}
      </div>
    </div>
  );
};
